import { useMemo, useState } from "react";
import { Search, ChevronRight } from "lucide-react";

import { useDashboard } from "@/lib/dataContext";
import { useSortableData } from "@/lib/useSortableData";
import { PageHeader } from "@/components/common/PageHeader";
import { SortHeader } from "@/components/common/SortHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { EntityDetailDialog } from "@/components/common/EntityDetailDialog";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { fmt, fmtDecimal, fmtPct, statusTone } from "@/lib/format";
import { cn } from "@/lib/utils";

// heat tint helper for youth % column
const heat = (pct) => {
    if (pct >= 30) return "bg-success/12 text-success";
    if (pct >= 22) return "bg-warning/14 text-warning";
    if (pct > 0) return "bg-destructive/12 text-destructive";
    return "text-muted-foreground";
};

export default function Districts() {
    const { data } = useDashboard();
    const [query, setQuery] = useState("");
    const [selected, setSelected] = useState(null);
    const [open, setOpen] = useState(false);

    const rows = useMemo(
        () =>
            data.district_drilldown.map((d) => ({
                id: d.district_id,
                name: d.district_name,
                units: d.units.total_units,
                members: d.membership.approved_members,
                youth_pct: d.membership.youth_share_pct,
                activities: d.full_year.reported_activities,
                attendance: d.full_year.total_attendance,
                avg: d.full_year.average_attendance_per_activity,
                beneficiaries: d.full_year.beneficiaries,
                yri: d.full_year.analytical_indices.youth_representation_index.value,
                yri_status: d.full_year.analytical_indices.youth_representation_index.status,
                raw: d,
            })),
        [data],
    );

    const filtered = useMemo(
        () => rows.filter((r) => r.name.toLowerCase().includes(query.toLowerCase())),
        [rows, query],
    );

    const { sorted, sortKey, sortDir, toggle } = useSortableData(filtered, "activities", "desc");

    const openRow = (r) => {
        setSelected(r.raw);
        setOpen(true);
    };

    return (
        <div className="space-y-6">
            <PageHeader
                eyebrow="Districts"
                title="District performance"
                description={`Compare all ${rows.length} districts. Click any row for a detailed profile.`}
            >
                <div className="relative w-full sm:w-64">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        placeholder="Search districts…"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        className="pl-9"
                    />
                </div>
            </PageHeader>

            <Card className="overflow-hidden border-border/70 bg-card shadow-card">
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="border-border/70 hover:bg-transparent">
                                <SortHeader label="District" k="name" sortKey={sortKey} sortDir={sortDir} onToggle={toggle} />
                                <SortHeader label="Units" k="units" sortKey={sortKey} sortDir={sortDir} onToggle={toggle} align="right" />
                                <SortHeader label="Members" k="members" sortKey={sortKey} sortDir={sortDir} onToggle={toggle} align="right" />
                                <SortHeader label="Youth %" k="youth_pct" sortKey={sortKey} sortDir={sortDir} onToggle={toggle} align="right" />
                                <SortHeader label="Activities" k="activities" sortKey={sortKey} sortDir={sortDir} onToggle={toggle} align="right" />
                                <SortHeader label="Attendance" k="attendance" sortKey={sortKey} sortDir={sortDir} onToggle={toggle} align="right" />
                                <SortHeader label="Avg" k="avg" sortKey={sortKey} sortDir={sortDir} onToggle={toggle} align="right" />
                                <SortHeader label="Youth Index" k="yri" sortKey={sortKey} sortDir={sortDir} onToggle={toggle} align="right" />
                                <TableCell className="w-8" />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {sorted.map((r) => (
                                <TableRow
                                    key={r.id}
                                    onClick={() => openRow(r)}
                                    className="cursor-pointer border-border/50 transition-colors hover:bg-secondary/50"
                                >
                                    <TableCell className="font-medium text-foreground">{r.name}</TableCell>
                                    <TableCell className="text-right tabular">{fmt(r.units)}</TableCell>
                                    <TableCell className="text-right tabular">{fmt(r.members)}</TableCell>
                                    <TableCell className="text-right">
                                        <span className={cn("inline-block rounded-md px-2 py-0.5 text-sm font-medium tabular", heat(r.youth_pct))}>
                                            {fmtPct(r.youth_pct)}
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-right tabular">{fmt(r.activities)}</TableCell>
                                    <TableCell className="text-right tabular">{fmt(r.attendance)}</TableCell>
                                    <TableCell className="text-right tabular">{fmtDecimal(r.avg)}</TableCell>
                                    <TableCell className="text-right">
                                        <StatusBadge tone={statusTone(r.yri_status)} label={fmtDecimal(r.yri, 2)} dot={false} />
                                    </TableCell>
                                    <TableCell className="text-right text-muted-foreground">
                                        <ChevronRight className="h-4 w-4" />
                                    </TableCell>
                                </TableRow>
                            ))}
                            {sorted.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={9} className="py-10 text-center text-muted-foreground">
                                        No districts match &ldquo;{query}&rdquo;
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
            </Card>

            <EntityDetailDialog entity={selected} type="district" open={open} onOpenChange={setOpen} />
        </div>
    );
}
